import React, { useState } from "react";
import { Lock, TrendingUp, TrendingDown, Heart, LineChart, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { TradeChartModal } from "./TradeChartModal";
import { SignalAiModal } from "./SignalAiModal";
import { Popover, PopoverTrigger, PopoverContent } from "./ui/popover";
import { useAuth } from "../context/AuthContext";

const ASSET_LABEL = { gold: "ORO", crypto: "CRYPTO", forex: "FOREX" };
const STATUS_STYLE = {
  active: "bg-blue-500/15 text-blue-400",
  tp_hit: "bg-emerald-500/15 text-emerald-400",
  sl_hit: "bg-red-500/15 text-red-400",
  be_hit: "bg-sky-400/20 text-sky-300",
  closed: "bg-zinc-500/15 text-zinc-400",
  cancelled: "bg-orange-500/15 text-orange-400",
};
const STATUS_LABEL = { active: "ATTIVO", tp_hit: "TAKE PROFIT", sl_hit: "STOP LOSS", be_hit: "BREAKEVEN", closed: "CHIUSO", cancelled: "CANCELLATO" };

const fmt = (v) => (v == null ? "—" : String(v).replace(".", ","));

const VERDICT_DOT = {
  hold: { cls: "bg-emerald-400", glow: "shadow-[0_0_7px_1px_rgba(52,211,153,0.75)]",
          label: "Verdetto Raeinexchange AI: CONGRUENTE col segnale (mantieni aperto)" },
  close: { cls: "bg-red-400", glow: "shadow-[0_0_7px_1px_rgba(248,113,113,0.75)]",
           label: "Verdetto Raeinexchange AI: OPPOSTO al segnale (chiudi)" },
};

function VerdictLegend() {
  const rows = [
    { c: "bg-emerald-400", t: "Verde", d: "Parere AI congruente col segnale (mantieni aperto)" },
    { c: "bg-red-400", t: "Rosso", d: "Parere AI opposto al segnale (chiudi)" },
    { c: "bg-amber-400", t: "Giallo", d: "Verdetto in aggiornamento / non ancora disponibile" },
  ];
  return (
    <div className="space-y-2.5" data-testid="verdict-legend">
      <div className="text-xs font-semibold text-white">Pallino Verdetto AI</div>
      <p className="text-[11px] text-zinc-400 leading-relaxed">
        Indica il parere di Raeinexchange AI su questo segnale rispetto alle ultime notizie economiche.
      </p>
      {rows.map((r) => (
        <div key={r.t} className="flex items-start gap-2">
          <span className={`w-2.5 h-2.5 rounded-full mt-1 shrink-0 ${r.c}`} />
          <div>
            <span className="text-[11px] font-medium text-white">{r.t}</span>
            <span className="text-[11px] text-zinc-400"> — {r.d}</span>
          </div>
        </div>
      ))}
      <div className="flex items-start gap-2">
        <span className="w-3.5 h-3.5 rounded-full mt-0.5 shrink-0 bg-white/10 border border-amber-500/40 flex items-center justify-center">
          <Lock size={8} className="text-amber-400" />
        </span>
        <div>
          <span className="text-[11px] font-medium text-white">Lucchetto</span>
          <span className="text-[11px] text-zinc-400"> — riservato agli abbonati VIP</span>
        </div>
      </div>
    </div>
  );
}

function AiVerdictDot({ s, locked }) {
  const computing = s.ai_verdict_status === "computing" || !s.ai_verdict;
  const v = locked ? null : (computing ? null : VERDICT_DOT[s.ai_verdict]);
  const score = locked ? null : s.ai_score;
  const scoreCls = score == null ? "" : score >= 7 ? "text-emerald-400" : score >= 5 ? "text-amber-400" : "text-red-400";
  return (
    <div className="flex flex-col items-end gap-1">
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={locked ? `ai-verdict-locked-${s.id}` : `ai-verdict-${s.id}`}
          onClick={(e) => e.stopPropagation()}
          className="flex items-center gap-1.5 cursor-pointer group"
        >
          <span className={`text-[10px] font-mono uppercase tracking-wider ${locked ? "text-zinc-600" : "text-zinc-500"} group-hover:text-zinc-300 transition-colors`}>
            Verdetto AI
          </span>
          {locked ? (
            <span className="w-3.5 h-3.5 rounded-full bg-white/10 border border-amber-500/40 flex items-center justify-center">
              <Lock size={8} className="text-amber-400" />
            </span>
          ) : (
            <span className={`w-2.5 h-2.5 rounded-full ${v ? v.cls : "bg-amber-400"} ${v ? v.glow : "shadow-[0_0_7px_1px_rgba(251,191,36,0.7)]"} ${computing ? "animate-pulse" : ""}`} />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" onClick={(e) => e.stopPropagation()}
        className="w-64 bg-[#141414] border-white/10 text-white">
        <VerdictLegend />
      </PopoverContent>
    </Popover>
      {!locked && score != null && (
        <span
          data-testid={`ai-score-${s.id}`}
          title="Voto Raeinexchange AI (più alto = ingresso più favorevole dopo SL consecutivi)"
          className={`font-mono text-xs font-semibold ${scoreCls}`}
        >
          {score}/10
        </span>
      )}
    </div>
  );
}

function Row({ label, value, color }) {
  return (
    <div className="flex justify-between items-center py-1.5 border-b border-white/5 last:border-0">
      <span className="text-xs text-zinc-500 uppercase tracking-wider">{label}</span>
      <span className={`font-mono text-sm ${color || "text-white"}`}>{value ?? "—"}</span>
    </div>
  );
}

function LevelRow({ label, price, pips, sign, color }) {
  return (
    <div className="flex justify-between items-center py-1.5 border-b border-white/5 last:border-0">
      <span className="text-xs text-zinc-500 uppercase tracking-wider">{label}</span>
      <span className={`font-mono text-sm ${color}`}>
        {fmt(price)}
        {pips != null && <span className="ml-2 text-xs opacity-80">({sign}{pips} pips)</span>}
      </span>
    </div>
  );
}

export default function SignalCard({ s, admin, onUpdate, onDelete, onToggleFavorite, hideAuto }) {
  const isBuy = s.direction === "BUY";
  const dirColor = isBuy ? "text-emerald-400" : "text-red-400";
  const dirBg = isBuy ? "bg-emerald-500/15" : "bg-red-500/15";
  const borderAccent = isBuy ? "border-l-emerald-500/60" : "border-l-red-500/60";
  const isClosed = s.status === "tp_hit" || s.status === "sl_hit" || s.status === "be_hit";
  const isPendingLimit = s.status === "active" && s.order_type === "LIMIT" && !s.filled;
  const statusBadge = (() => {
    if (isPendingLimit) return { label: "PENDENTE", cls: "bg-white/10 text-white" };
    if (s.status === "active" && s.sl_in_profit)
      return { label: "SL IN PROFIT", cls: "bg-emerald-400/20 text-emerald-300" };
    if (s.status === "active" && s.breakeven)
      return { label: "SL a BE", cls: "bg-amber-400/20 text-amber-300" };
    if (s.status === "active" && s.order_type === "LIMIT" && s.filled)
      return { label: "FILLED", cls: "bg-blue-500/15 text-blue-400" };
    return { label: STATUS_LABEL[s.status] || s.status, cls: STATUS_STYLE[s.status] };
  })();
  const canViewChart = !s.locked && (isClosed || s.status === "active");
  const canAnalyze = !s.locked && s.status === "active";
  const { user } = useAuth();
  const isVip = !!(user && (user.role === "admin" || user.is_premium));
  const aiFree = (s.symbol || "").toUpperCase() === "XAUUSD" && ["M15", "H1"].includes(s.timeframe);
  const aiLocked = !isVip && !aiFree;
  const [chartOpen, setChartOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  const renderLevels = () => {
    if (s.locked) {
      return (
        <Link to="/pricing" data-testid={`signal-locked-${s.id}`}
          className="flex flex-col items-center justify-center gap-2 py-6 rounded-md border border-dashed border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/10 transition-colors">
          <Lock size={20} className="text-amber-400" />
          <span className="text-sm text-amber-400 font-medium">Segnale Premium — Sblocca ora</span>
        </Link>
      );
    }
    if (s.order_type === "LIMIT" || s.order_type === "NOW") {
      return (
        <div className="space-y-0">
          {s.order_type === "LIMIT" && <div className="text-xs text-emerald-400 mb-2 font-mono">Order placed successfully</div>}
          <Row label={s.entry_label || "Entry"} value={fmt(s.entry)} />
          <LevelRow label="TP" price={s.tp_price} pips={s.tp_pips} sign="+" color="text-emerald-400" />
          <LevelRow label="SL" price={s.sl_price} pips={s.sl_pips} sign="-" color="text-red-400" />
        </div>
      );
    }
    return (
      <div className="space-y-0">
        <Row label="Entry" value={fmt(s.entry)} />
        <LevelRow label="Stop Loss" price={s.sl_price ?? s.stop_loss} pips={s.sl_pips} sign="-" color="text-red-400" />
        <LevelRow label="Take Profit 1" price={s.tp_price ?? s.take_profit_1} pips={s.tp_pips} sign="+" color="text-emerald-400" />
        {s.take_profit_2 != null && <Row label="Take Profit 2" value={fmt(s.take_profit_2)} color="text-emerald-400" />}
      </div>
    );
  };

  return (
    <>
    <div
      data-testid={`signal-card-${s.id}`}
      onClick={canViewChart ? () => setChartOpen(true) : undefined}
      className={`bg-[#141414] border border-white/10 border-l-2 ${borderAccent} rounded-md p-5 animate-in-up transition-colors hover:bg-[#1a1a1a] ${canViewChart ? "cursor-pointer" : ""}`}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-lg font-semibold tracking-tight text-white">{s.symbol}</span>
            {s.is_premium && (
              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-amber-500/15 text-amber-400 uppercase tracking-wider">Premium</span>
            )}
            {s.auto && !hideAuto && (
              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-blue-500/15 text-blue-400 uppercase tracking-wider">Auto</span>
            )}
          </div>
          <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-white/5 text-zinc-400 uppercase tracking-wider">
            {ASSET_LABEL[s.asset_class] || s.asset_class} · {s.timeframe}{s.strategy_label ? ` · ${s.strategy_label}` : ""}
          </span>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          <div className={`flex items-center gap-1 px-2.5 py-1 rounded-sm ${dirBg} ${dirColor}`}>
            {isBuy ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            <span className="font-mono text-xs font-semibold tracking-wider">{s.direction}{s.order_type ? ` ${s.order_type === "LIMIT" && s.filled ? "FILLED" : s.order_type}` : ""}</span>
          </div>
          <AiVerdictDot s={s} locked={aiLocked} />
        </div>
      </div>

      {renderLevels()}

      <div className="flex items-center justify-between mt-4">
        <div className="flex items-center gap-2">
          <span data-testid={`signal-status-${s.id}`} className={`text-[10px] font-mono px-2 py-0.5 rounded-sm uppercase tracking-wider ${statusBadge.cls}`}>
            {statusBadge.label}
          </span>
          {canViewChart && (
            <span data-testid={`view-chart-hint-${s.id}`} className="flex items-center gap-1 text-[10px] font-mono text-zinc-500">
              <LineChart size={12} /> Vedi grafico
            </span>
          )}
        </div>
        {canAnalyze && (
          <button
            data-testid={`signal-ai-btn-${s.id}`}
            onClick={(e) => { e.stopPropagation(); setAiOpen(true); }}
            className="flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/15 hover:border-emerald-500/70 transition-colors"
            title={aiLocked ? "Raeinexchange AI (VIP)" : "Analizza con Raeinexchange AI"}
          >
            {aiLocked ? <Lock size={11} /> : <Sparkles size={11} />} Raeinexchange AI
          </button>
        )}
        <div className="flex flex-col items-end gap-1.5">
          {onToggleFavorite && (
            <button
              data-testid={`favorite-btn-${s.id}`}
              onClick={(e) => { e.stopPropagation(); onToggleFavorite(s.id, s.is_favorite); }}
              aria-label={s.is_favorite ? "Rimuovi dai preferiti" : "Aggiungi ai preferiti"}
              className={`p-1 rounded-sm transition-colors ${
                s.is_favorite ? "text-rose-400 hover:text-rose-300" : "text-zinc-500 hover:text-rose-400"}`}
            >
              <Heart size={16} fill={s.is_favorite ? "currentColor" : "none"} />
            </button>
          )}
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between text-[11px] font-mono text-zinc-500 tracking-wider">
        <span data-testid={`signal-id-${s.id}`}>{s.seq != null ? `ID ${s.seq}` : ""}</span>
        <span data-testid={`signal-date-${s.id}`}>
          {new Date(s.created_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" })}
        </span>
      </div>

      {admin && (
        <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-white/5">
          <button data-testid={`mark-tp-${s.id}`} onClick={(e) => { e.stopPropagation(); onUpdate(s.id, { status: "tp_hit" }); }}
            className="text-xs px-2 py-1 rounded-sm bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 transition-colors">TP</button>
          <button data-testid={`mark-sl-${s.id}`} onClick={(e) => { e.stopPropagation(); onUpdate(s.id, { status: "sl_hit" }); }}
            className="text-xs px-2 py-1 rounded-sm bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors">SL</button>
          <button data-testid={`mark-closed-${s.id}`} onClick={(e) => { e.stopPropagation(); onUpdate(s.id, { status: "closed" }); }}
            className="text-xs px-2 py-1 rounded-sm bg-white/5 text-zinc-400 hover:bg-white/10 transition-colors">Chiudi</button>
          <button data-testid={`delete-signal-${s.id}`} onClick={(e) => { e.stopPropagation(); onDelete(s.id); }}
            className="text-xs px-2 py-1 rounded-sm bg-white/5 text-zinc-400 hover:bg-red-500/20 hover:text-red-400 transition-colors ml-auto">Elimina</button>
        </div>
      )}
    </div>

    {canViewChart && (
      <TradeChartModal signal={s} open={chartOpen} onOpenChange={setChartOpen} />
    )}
    {canAnalyze && (
      <SignalAiModal signal={s} open={aiOpen} onClose={() => setAiOpen(false)} locked={aiLocked} />
    )}
    </>
  );
}
